import * as React from 'react'
import { createPortal } from 'react-dom'
import classNames from 'classnames'
import { AnimatePresence, motion } from 'framer-motion'
import { useClickAway } from 'ahooks'
import styled from 'styled-components'
import { fadeConfig } from '../../config/motion'
import { cloneElement } from '../_util/reactNode'

const Wrapper = styled.div`
  /* global */
  transition: all 0.3s ease-in-out;
  &.follow {
    position: relative;
  }
  > .inside {
    .placeholder {
      color: rgba(255, 255, 255, 0.45);
      transition: all 0.3s ease-in-out;
    }
    .selection {
      color: rgba(255, 255, 255, 0.65);
      font-weight: 400;
      transition: all 0.3s ease-in-out;
    }
    .arrow {
      margin-left: 4px;
      width: 20px;
      height: 20px;
      background: rgba(255, 255, 255, 0.08);
      border-radius: 4px;
      svg {
        fill: rgba(255, 255, 255, 0.65);
        transition: all 0.3s ease-in-out;
      }
    }
  }
  &:not(.danger, .disabled).visible {
    .arrow {
      svg {
        transform: rotateZ(-180deg);
      }
    }
  }
  &:not(.danger, .disabled):hover {
    cursor: pointer;
    .selection {
      opacity: 0.6;
    }
  }
  /* base */
  &.danger {
    color: #ff7878;
    .arrow {
      fill: #ff7878;
    }
  }
  &.md {
    border-radius: 5px;
    .inside {
      padding: 0 10px;
      height: 38px;
      .placeholder,
      .selection {
        font-size: 14px;
      }
    }
  }
`

const Trigger = styled(motion.div)`
  /* global */
  position: absolute;
  background: #3f3a3a;
  box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.4);
  overflow: hidden;
  z-index: 1;
  transition: background-color box-shadow border 0.3s ease-in-out;
  .ms-container {
    width: 100%; // 注意边框侵占容器宽度导致样式错误
    max-height: 40vh;
  }
  li {
    cursor: pointer;
    transition: all 0.3s ease-in-out;
    &:hover {
      background: #f4c470;
      color: #000;
    }
  }
  .active {
    background: #f4c470;
    color: #000;
  }
  /* base */
  &.md {
    border-radius: 8px;
    li {
      list-style: none;
      padding: 0 10px;
      height: 38px;
      line-height: 38px;
      font-size: 14px;
    }
  }
`

const DIRECTION_MAP = {
  left: 'start' as const,
  right: 'end' as const,
}

type DropdownDirection = 'top' | 'bottom' // 显示位置

type DropdownPlacement = 'left' | 'right'

type SizeType = 'sm' | 'md' | 'lg'

export interface SelectProps {
  className?: string
  direction?: DropdownDirection
  placement?: DropdownPlacement
  follow?: boolean
  size?: SizeType
  danger?: boolean
  disabled?: boolean
  placeholder?: string
  suffix?: React.ReactNode
  triggerWidth?: number
  triggerHeight?: number
  overlay?: React.ReactNode
  children?: React.ReactNode
}

interface TriggerProps extends SelectProps {
  followRef: React.RefObject<HTMLDivElement>
  onClose: (...args: any[]) => any
}

interface PositionProps {
  top?: number
  bottom?: number
  left?: number
  right?: number
  width: number | 'auto'
  height?: number
}

const IconArrow: React.FC<React.SVGAttributes<SVGElement>> = (props) => {
  return (
    <svg width="8" height="5" viewBox="0 0 8 5" {...props}>
      <path
        // eslint-disable-next-line
        d="M0.707105 1.70711C0.0771402 1.07714 0.523309 0 1.41421 0H6.58579C7.47669 0 7.92286 1.07714 7.29289 1.70711L4.70711 4.29289C4.31658 4.68342 3.68342 4.68342 3.29289 4.29289L0.707105 1.70711Z"
      />
    </svg>
  )
}

const Portal: React.FC<TriggerProps> = (props: TriggerProps) => {
  const {
    followRef,
    direction = 'bottom',
    placement = 'left',
    size = 'md',
    follow,
    triggerWidth,
    triggerHeight,
    children,
    onClose,
  } = props

  const innerRef = React.useRef<HTMLDivElement>(null)

  const [directionValue, setDirectionValue] = React.useState<PositionProps>()
  const extra = DIRECTION_MAP[placement]

  const filterPosition = React.useCallback(
    (space: DOMRect, inside: DOMRect) => {
      const { top: followTop, left: followLeft, width: followWidth, height: followHeight } = space
      const { width: innerWidth } = inside
      const siteMap = {
        start: followLeft,
        end: followLeft + followWidth - innerWidth,
      }
      const directionMap = {
        top: followHeight + 4,
        bottom: (follow ? 0 : followTop) + followHeight + 4,
      }
      const rectSize: PositionProps = {
        // top: (follow ? 0 : followTop) + followHeight + 4,
        [direction === 'bottom' ? 'top' : 'bottom']: directionMap[direction],
        [placement]: follow ? -1 : siteMap[extra],
        width: placement === 'left' ? triggerWidth || followWidth : 'auto',
      }
      if (triggerHeight) rectSize.height = triggerHeight

      return rectSize
    },
    [follow, direction, placement, extra, triggerHeight, triggerWidth],
  )

  React.useEffect(() => {
    if (!followRef.current || !innerRef.current) return
    const space = followRef.current.getBoundingClientRect()
    const inside = innerRef.current.getBoundingClientRect()
    const result = filterPosition(space, inside)
    setDirectionValue(result)
  }, [followRef, innerRef, filterPosition])

  const selectTrigger = React.useMemo(() => {
    if (!followRef.current) return null
    return (
      <Trigger
        className={size}
        ref={innerRef}
        style={directionValue}
        onClick={(e) => e.stopPropagation()}
        {...fadeConfig}
      >
        {cloneElement(children, { onClick: onClose })}
      </Trigger>
    )
  }, [followRef, size, directionValue, children, onClose])

  useClickAway(() => onClose?.(), followRef)

  const DOM = (follow && followRef.current ? followRef.current : window.document.body) as HTMLElement
  return createPortal(selectTrigger, DOM)
}

const Select: React.FC<SelectProps> = (props: SelectProps) => {
  const {
    className,
    follow = false,
    size = 'md',
    danger = false,
    disabled = false,
    placeholder,
    suffix,
    overlay,
  } = props

  const followRef = React.useRef<HTMLDivElement>(null)
  const [visible, setVisible] = React.useState<boolean>(false)

  const classes = classNames(className, {
    follow,
    [`${size}`]: size,
    danger,
    disabled,
    visible,
  })

  // handle event
  const handleOpen: React.MouseEventHandler<HTMLDivElement> = () => {
    if (disabled) return
    setVisible(true)
  }

  const handleClose: React.MouseEventHandler<HTMLDivElement> = () => {
    if (disabled) return
    setVisible(false)
  }

  const memoElement = React.useMemo(() => {
    if (!React.isValidElement(overlay)) return <span className="placeholder">{placeholder}</span>
    const renderClone = cloneElement(overlay, {
      className: `selection ${overlay.props.className ?? ''}`.trimEnd(),
    })
    return renderClone
  }, [overlay, placeholder])

  return (
    <>
      <Wrapper className={classes} ref={followRef} onClick={handleOpen}>
        <div className="inside row-between">
          {memoElement}
          {suffix ?? (
            <div className="arrow row-center">
              <IconArrow />
            </div>
          )}
        </div>
      </Wrapper>
      <AnimatePresence>{visible && <Portal {...props} followRef={followRef} onClose={handleClose} />}</AnimatePresence>
    </>
  )
}

export default Select
